import { useState, useEffect, useRef } from "react";
import { colors } from "./constants";
import { navigateBible } from "./routing";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortDown } from "@fortawesome/free-solid-svg-icons";
import { useScreenSize } from "../hooks/useScreenSize";

interface BibleNavigationItemProps {
  option: any;
  title: string;
  chosenValue: any;
  isLast: boolean;
  onSelect: (value: any) => void;
}

function BibleNavigationItem({
  option,
  title,
  chosenValue,
  isLast,
  onSelect
}: BibleNavigationItemProps) {
  const itemRef = useRef<HTMLDivElement>(null);
  const hasScrolledOnMount = useRef(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.1 }
    );

    if (itemRef.current) {
      observer.observe(itemRef.current);
    }

    return () => {
      if (itemRef.current) {
        observer.unobserve(itemRef.current);
      }
    };
  }, []);

  const isChosen = option.value === chosenValue;

  // scroll to the element if it's chosen and not visible - only on initial load
  useEffect(() => {
    if (isVisible) {
      hasScrolledOnMount.current = true;
    }
    if (isChosen && !isVisible && !hasScrolledOnMount.current) {
      itemRef.current?.scrollIntoView({
        block: 'center',
      });
      hasScrolledOnMount.current = true;
    }
  }, [isChosen, isVisible]);

  return (
    <div
      ref={itemRef}
      key={`nav-col-option-${option.label}-${option.value}`}
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: isLast ? '' : `1px solid ${colors['borderColor']}`,
        backgroundColor: isChosen ? colors['light'] : colors['white'],
        color: isChosen ? colors['darkPrimary'] : colors['black'],
        padding: '0px 4px',
        cursor: 'pointer',
      }}
      onClick={() => onSelect(option.value)}
    >
      <div>
        {option.label}
      </div>
      <input
        type="radio"
        name={title}
        value={option.value}
        style={{
          cursor: 'pointer',
          border: `1px solid ${colors['borderColor']}`,
          borderRadius: '0%',
        }}
        checked={option.value === chosenValue}
        readOnly
      />
    </div>
  );
}

function BibleNavigationColumn({
  last,
  title,
  options,
  chosenValue,
  resetScrollKey,
  style,
  titleStyle,
  onSelect
}: any) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  // Skip the reset on mount so it doesn't fight the chosen item's scrollIntoView -
  // only reset when resetScrollKey actually changes after that (e.g. a new book chosen).
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [resetScrollKey]);

  return (
    <div
      style={{
        borderRight: last ? '' : `1px solid ${colors['borderColor']}`,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        ...style
      }}
    >
      <div style={{
        backgroundColor: colors['light'],
        padding: '0px 4px',
        borderBottom: `1px solid ${colors['borderColor']}`,
        color: colors['darkPrimary'],
        ...titleStyle
      }}>
        {title}
      </div>
      <div
        ref={scrollContainerRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        {options.map((option: any, index: number) => (
          <BibleNavigationItem
            key={`nav-col-option-${option.label}-${option.value}`}
            option={option}
            title={title}
            chosenValue={chosenValue}
            isLast={index === options.length - 1}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  )
}

interface BibleNavigationProps {
  currentTranslation: any;
  currentBook: any;
  currentChapter: any;
  books: any[];
  translations: any[];
}

export function BibleNavigation({
  currentTranslation,
  currentBook,
  currentChapter,
  books,
  translations
}: BibleNavigationProps) {
  const [open, setOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState(currentBook);
  const [selectedChapter, setSelectedChapter] = useState(currentChapter?.chapter_number || null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const currentLocation = {
    translationSlug: currentTranslation.slug,
    bookSlug: currentBook.slug,
    chapterNumber: currentChapter.chapter_number,
  };

  // Close dialog when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [open]);

  const { screenLargerThan } = useScreenSize()
  // On narrow screens the bar has to fit prev/next/search around this control, so
  // everything here shrinks: tighter padding, smaller type, and a title that can
  // ellipsize instead of pushing the buttons off-screen.
  const compact = !screenLargerThan('lg')
  return (
    <div style={{
      gap: '4px',
      display: 'flex',
      flexDirection: 'row',
      minWidth: 0,
      flexShrink: compact ? 1 : 0,
    }}>

      <div ref={dialogRef} style={{ minWidth: 0, flexShrink: compact ? 1 : 0 }}>
        <div
          style={{
            height: '100%',
            border: `1px solid ${colors['borderColor']}`,
            cursor: 'pointer',
            fontFamily: 'Times New Roman, serif',
            color: colors['darkPrimary'],
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            padding: compact ? '0px 4px' : '0px 8px',
            minWidth: 0,
          }}
          onClick={() => setOpen(!open)}
        >
          <div style={{
            marginRight: compact ? '4px' : '8px',
            borderRight: `1px solid ${colors['borderColor']}`,
            paddingRight: compact ? '4px' : '8px',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            color: colors['black'],
            justifyContent: 'center',
            flex: '0 0 auto',

            fontSize: compact ? '14px' : '18px',
          }}>
            {currentTranslation.slug}
            <FontAwesomeIcon icon={faSortDown} style={{
              fontSize: '8px',
              marginLeft: compact ? '6px' : '10px',
            }} />
          </div>
          <div style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            color: colors['black'],
            justifyContent: 'flex-start',
            minWidth: 0,

            fontSize: compact ? '20px' : '25px',
            // if the text overflows, add ellipsis
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            <span style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}>
              {`${currentBook.name} ${currentChapter.chapter_number}`}
            </span>
            <FontAwesomeIcon icon={faSortDown} style={{
              fontSize: '10px',
              marginLeft: compact ? '6px' : '10px',
              flex: '0 0 auto',
            }} />
          </div>
        </div>
        {open && (
          <div
            style={{
              border: `1px solid ${colors['borderColor']}`,
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'row',
              backgroundColor: colors['background'],
              // width: '40%',
              width: screenLargerThan('xl') ? '60%' : (screenLargerThan('md') ? '75%' : '90%'),
              height: screenLargerThan('md') ? '500px' : '70vh',
              maxHeight: screenLargerThan('md') ? '500px' : '70vh',
              overflow: 'hidden',
              position: 'absolute',
              marginTop: '4px',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                height: '100%',
              }}
            >
              <BibleNavigationColumn
                title="Translation"
                options={translations.map((translation: any) => ({
                  value: translation.slug,
                  label: translation.name
                }))}
                chosenValue={currentTranslation.slug}
                onSelect={(translationSlug: string) => {
                  navigateBible(currentLocation, { translationSlug });
                  setOpen(false);
                }}
                style={{
                  flex: 1
                }}
              />
              {screenLargerThan('md') ? (
                <>
                  <BibleNavigationColumn
                    title="Old Testament"
                    options={books.filter((book) => book.testament == 'old').map((book: any) => ({
                      value: book.slug,
                      label: book.name
                    }))}
                    chosenValue={selectedBook.slug}
                    onSelect={(book_slug: string) => {
                      const newBook = books.find(book => book.slug === book_slug);
                      setSelectedBook(newBook);
                      setSelectedChapter(null); // Clear chapter selection
                    }}
                    style={{
                      flex: 1.25
                    }}
                  />
                  <BibleNavigationColumn
                    title="New Testament"
                    options={books.filter((book) => book.testament == 'new').map((book: any) => ({
                      value: book.slug,
                      label: book.name
                    }))}
                    chosenValue={selectedBook.slug}
                    onSelect={(book_slug: string) => {
                      const newBook = books.find(book => book.slug === book_slug);
                      setSelectedBook(newBook);
                      setSelectedChapter(null); // Clear chapter selection
                    }}
                    style={{
                      flex: 1.25
                    }}
                  />
                </>
              ) : (
                <BibleNavigationColumn
                  title="Books"
                  options={books.map((book: any) => ({
                    value: book.slug,
                    label: book.name
                  }))}
                  chosenValue={selectedBook.slug}
                  onSelect={(book_slug: string) => {
                    const newBook = books.find(book => book.slug === book_slug);
                    setSelectedBook(newBook);
                    setSelectedChapter(null); // Clear chapter selection
                  }}
                  style={{
                    flex: 1.25
                  }}
                />
              )}
              <BibleNavigationColumn
                title="Chapter"
                options={selectedBook['bible_chapters'].map((chapter: any) => ({
                  value: chapter.chapter_number,
                  label: chapter.chapter_number
                }))}
                chosenValue={selectedChapter}
                resetScrollKey={selectedBook.slug}
                onSelect={(chapterNumber: number) => {
                  setSelectedChapter(chapterNumber);
                  navigateBible(currentLocation, {
                    bookSlug: selectedBook.slug,
                    chapterNumber: chapterNumber,
                  });
                  setOpen(false);
                }}
                last={true}
                style={{
                  flex: 0.75,
                }}
                titleStyle={{
                  backgroundColor: selectedBook && !selectedChapter ? colors['darkPrimary'] : colors['light'],
                  color: selectedBook && !selectedChapter ? colors['light'] : colors['darkPrimary'],
                }}
              />
            </div>
          </div>
        )}
      </div>

    </div>
  )
}

interface TranslationSelectProps {
  currentTranslation: any;
  currentBook: any;
  currentChapter: any;
  translations: any[];
}

export function TranslationSelect({
  currentTranslation,
  currentBook,
  currentChapter,
  translations
}: TranslationSelectProps) {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  const currentLocation = {
    translationSlug: currentTranslation.slug,
    bookSlug: currentBook.slug,
    chapterNumber: currentChapter.chapter_number,
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [open]);

  return (
    <div ref={dialogRef} style={{ position: 'relative' }}>
      <div
        style={{
          height: '100%',
          border: `1px solid ${colors['borderColor']}`,
          cursor: 'pointer',
          fontFamily: 'Times New Roman, serif',
          color: colors['black'],
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0px 8px',
          fontSize: '18px',
        }}
        onClick={() => setOpen(!open)}
      >
        {currentTranslation.slug}
        <FontAwesomeIcon icon={faSortDown} style={{
          fontSize: '8px',
          marginLeft: '8px',
        }} />
      </div>
      {open && (
        <div
          style={{
            border: `1px solid ${colors['borderColor']}`,
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            zIndex: 1000,
            backgroundColor: colors['background'],
            minWidth: '150px',
            maxHeight: '60vh',
            overflowY: 'auto',
            position: 'absolute',
            marginTop: '4px',
            left: 0,
          }}
        >
          {translations.map((translation: any, index: number) => (
            <div
              key={`translation-option-${translation.slug}`}
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: index === translations.length - 1 ? '' : `1px solid ${colors['borderColor']}`,
                backgroundColor: translation.slug === currentTranslation.slug ? colors['light'] : colors['white'],
                color: translation.slug === currentTranslation.slug ? colors['darkPrimary'] : colors['black'],
                padding: '4px 8px',
                cursor: 'pointer',
              }}
              onClick={() => {
                navigateBible(currentLocation, { translationSlug: translation.slug });
                setOpen(false);
              }}
            >
              {translation.name}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}